// Poster-style cover themes for the one-screen creator. When the host doesn't
// upload a photo, the chosen theme is drawn to a 1200x1200 PNG and uploaded as
// the event image, so every list, card and share preview has a real picture.

export const COVER_THEMES = [
  { id: "night", name: "Night", bg: "#151033", ink: "#FFFFFF", accent: "#FF5A1F" },
  { id: "red", name: "Red", bg: "#CC1F21", ink: "#FFFFFF", accent: "#0B0F19" },
  { id: "mint", name: "Mint", bg: "#C9F2DE", ink: "#0B2A1C", accent: "#0F7A4C" },
  { id: "gold", name: "Gold", bg: "#1A1207", ink: "#F4D58A", accent: "#D4A017" },
  { id: "paper", name: "Paper", bg: "#F4EFE6", ink: "#1B1B1B", accent: "#CC1F21" },
  { id: "ocean", name: "Ocean", bg: "#0B2E4F", ink: "#E6F4FF", accent: "#3BC9F5" },
];

export function findTheme(id) {
  return COVER_THEMES.find((t) => t.id === id) || COVER_THEMES[0];
}

export function coverDateLabel(startDate, startTime) {
  if (!startDate) return "SAVE THE DATE";
  const d = new Date(`${startDate}T${startTime || "00:00"}:00`);
  if (Number.isNaN(d.getTime())) return "SAVE THE DATE";
  return d
    .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    .replace(",", " ·")
    .toUpperCase();
}

// Split a title into at most 4 lines that fit `maxWidth` at the given font.
function wrap(ctx, text, maxWidth) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width <= maxWidth || !line) line = next;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Draws the poster and resolves a PNG File ready for upload. */
export async function renderCoverFile({ themeId, title, startDate, startTime }) {
  const t = findTheme(themeId);
  const S = 1200;
  const canvas = document.createElement("canvas");
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");
  try {
    if (document.fonts?.load) await document.fonts.load("800 120px Outfit");
  } catch (_) {}

  ctx.fillStyle = t.bg;
  ctx.fillRect(0, 0, S, S);

  // date
  ctx.fillStyle = t.ink;
  ctx.font = "800 40px Outfit, Arial, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(coverDateLabel(startDate, startTime), 80, 84);

  // accent dot + bar
  ctx.fillStyle = t.accent;
  ctx.beginPath();
  ctx.arc(S - 150, 150, 70, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(80, S - 130, 280, 26);

  // title, as big as fits
  const maxW = S - 160;
  let size = 170;
  let lines = [];
  for (; size >= 70; size -= 10) {
    ctx.font = `800 ${size}px Outfit, Arial, sans-serif`;
    lines = wrap(ctx, title || "Your event", maxW);
    if (lines.length <= 4 && lines.every((l) => ctx.measureText(l).width <= maxW)) break;
  }
  ctx.fillStyle = t.ink;
  ctx.textBaseline = "alphabetic";
  const lineH = size * 0.98;
  let y = S - 190 - (lines.length - 1) * lineH;
  for (const l of lines.slice(0, 4)) {
    ctx.fillText(l, 80, y);
    y += lineH;
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return new File([blob], `cover-${t.id}.png`, { type: "image/png" });
}
